class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Desktop-only hover open behavior
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._updateHoverBindings = this._updateHoverBindings.bind(this);
    this._onSubmenuEnter = this._onSubmenuEnter.bind(this);
    this._onSubmenuLeave = this._onSubmenuLeave.bind(this);

    // Match desktop and devices that support hover
    this._mqlHover = window.matchMedia('(hover: hover)');
    this._mqlDesktop = window.matchMedia('(min-width: 990px)');
    this._hoverBound = false;
    this._isPointerInside = false;

    // Timers for smoother UX
    this._hoverOpenTimeout = null;
    this._hoverCloseTimeout = null;

    // Initialize and keep in sync on viewport changes
    this._updateHoverBindings();
    this._mqlHover.addEventListener('change', this._updateHoverBindings);
    this._mqlDesktop.addEventListener('change', this._updateHoverBindings);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  _onMouseEnter() {
    // Open details on hover for desktop (with a small intent delay)
    clearTimeout(this._hoverCloseTimeout);
    clearTimeout(this._hoverOpenTimeout);
    this._hoverOpenTimeout = setTimeout(() => this._open(), 120);
    this._isPointerInside = true;
  }

  _onMouseLeave(evt) {
    // Close details when leaving (with longer delay to avoid flicker)
    // If moving to another element inside this component, do nothing
    const toEl = evt && evt.relatedTarget;
    if (toEl && this.contains(toEl)) return;
    clearTimeout(this._hoverOpenTimeout);
    clearTimeout(this._hoverCloseTimeout);
    this._hoverCloseTimeout = setTimeout(() => this._close(), 260);
    this._isPointerInside = false;
  }

  _updateHoverBindings() {
    const shouldBind = this._mqlHover.matches && this._mqlDesktop.matches;
    if (shouldBind && !this._hoverBound) {
      const submenu = this.mainDetailsToggle.querySelector('.header__submenu');
      // Enter by hovering the entire details area (summary + submenu)
      this.mainDetailsToggle.addEventListener('mouseenter', this._onMouseEnter);
      // Leave only when the pointer exits the entire details area
      this.mainDetailsToggle.addEventListener('mouseleave', this._onMouseLeave);
      // Additionally ensure we keep it open while traversing submenu
      if (submenu) {
        submenu.addEventListener('mouseenter', this._onSubmenuEnter);
        submenu.addEventListener('mouseleave', this._onSubmenuLeave);
      }
      this._hoverBound = true;
    } else if (!shouldBind && this._hoverBound) {
      const submenu = this.mainDetailsToggle.querySelector('.header__submenu');
      this.mainDetailsToggle.removeEventListener('mouseenter', this._onMouseEnter);
      this.mainDetailsToggle.removeEventListener('mouseleave', this._onMouseLeave);
      if (submenu) {
        submenu.removeEventListener('mouseenter', this._onSubmenuEnter);
        submenu.removeEventListener('mouseleave', this._onSubmenuLeave);
      }
      this._hoverBound = false;
    }
  }

  _onSubmenuEnter() {
    // keep open while hovering submenu
    this._onMouseEnter();
  }

  _onSubmenuLeave() {
    this._onMouseLeave();
  }

  _open() {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', true);
    }
  }

  _close() {
    // Respect hover state: don't close if pointer is still inside on desktop hover mode
    if (this._hoverBound && this._isPointerInside) return;
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.removeAttribute('open');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', false);
    }
  }
}

customElements.define('header-menu', HeaderMenu);
